import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import {
  CohortDescriptions,
  CohortLetters,
  CohortDefaultIndividualServices,
  IndividualServiceTypes,
  ServiceLabels,
  type CohortLetter,
  type IndividualServiceType,
  type AiSuggestionAction,
} from '@census/shared';
import { IntakePreview } from './IntakePreview';
import { Modal } from '../../ui/Modal';
import { todayLocalIso } from '../../shared/dates';
import { useCohorts } from '../../shared/cohorts';

interface Draft {
  firstName: string;
  lastName: string;
  cohort: CohortLetter;
  telehealthAllowed: boolean;
  homeCommunityOk: boolean;
  preferredDays: string[];
  // Resource id of a specific IOP group track (e.g. "IOP — Track A (Morning)").
  // Optional — when omitted the engine picks the lowest-enrolment track.
  iopTrackResourceId?: string;
  nursingGroupDay?: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI';
  // When true, the engine auto-picks the least-full nursing day (mutually
  // exclusive with an explicit nursingGroupDay).
  nursingDayAuto?: boolean;
  // When true, nursing group is not required for this client.
  nursingSkip?: boolean;
  // When true, no individual services are required (clears requiredServices).
  servicesNotNeeded?: boolean;
  requiredServices: IndividualServiceType[];
  intakeDate: string;
  externalRef?: string;
  caseManagerResourceId?: string;
  // Clinic location the client belongs to. Seeded from the housing
  // pending-link's clinicLocationId when this intake came from an In-Pipeline
  // housing resident. Sent to the engine so a provider at the same location
  // gets the same-location priority bonus.
  locationId?: string;
  // Per-service constraints accumulated by clicking "Apply" on AI suggestions
  // in the Preview modal. The engine honors these at preview + commit; they
  // are NOT persisted onto the Client after commit.
  serviceConstraints?: {
    excludedDaysByService: Record<string, string[]>;
    excludedResourcesByService: Record<string, string[]>;
    pinnedDayByService: Record<string, string>;
    pinnedResourceByService: Record<string, string>;
    // Service types the engine must place WITHOUT overlap, even in the
    // SCHEDULER_ALLOW_OVERLAP test mode. Driven by the AI "AVOID_OVERLAP"
    // suggestion — the per-service lever for resolving a double-book.
    avoidOverlapServices: string[];
  };
}

function emptyServiceConstraints(): NonNullable<Draft['serviceConstraints']> {
  return {
    excludedDaysByService: {},
    excludedResourcesByService: {},
    pinnedDayByService: {},
    pinnedResourceByService: {},
    avoidOverlapServices: [],
  };
}

/** Deep-clone the current draft constraints into a fresh mutable object. */
function cloneConstraints(
  cur: NonNullable<Draft['serviceConstraints']>,
): NonNullable<Draft['serviceConstraints']> {
  return {
    excludedDaysByService: { ...cur.excludedDaysByService },
    excludedResourcesByService: { ...cur.excludedResourcesByService },
    pinnedDayByService: { ...cur.pinnedDayByService },
    pinnedResourceByService: { ...cur.pinnedResourceByService },
    avoidOverlapServices: [...(cur.avoidOverlapServices ?? [])],
  };
}

export function IntakeWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Pre-fill from a Pipeline candidate if the operator clicked "Promote".
  // location.state.fromCandidate is { candidateId, externalRef, firstName, lastName, suggestedCohort? }
  const fromCandidate = (location.state as any)?.fromCandidate as
    | { candidateId?: string; externalRef?: string; firstName?: string; lastName?: string; suggestedCohort?: CohortLetter }
    | undefined;

  const { cohorts: cohortList, byCode: cohortByCode } = useCohorts({ activeOnly: true });
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2>(1);
  // Reactive AI-complete flag, driven by the checklist. Commit is blocked
  // until BOTH GPT-4o and Claude have finished. Reset to false on every
  // preview run (see preview.onMutate) so a re-run re-disables Commit.
  const [aiComplete, setAiComplete] = useState(false);
  const [previewEpoch, setPreviewEpoch] = useState(0);
  const aiSectionRef = useRef<HTMLElement | null>(null);

  const [showTelehealthModal, setShowTelehealthModal] = useState(false);
  const [commitErrorMsg, setCommitErrorMsg] = useState<string | null>(null);

  // Policy override modal state. When the operator clicks "Commit intake" and
  // there are POLICY violations, we show them one at a time in a modal instead
  // of the native browser prompt(). pendingPolicies is the queue; each
  // confirmation pops the head and accumulates the reason into collectedReasons.
  const [pendingPolicies, setPendingPolicies] = useState<Array<{ code: string; message: string }>>([]);
  const [collectedReasons, setCollectedReasons] = useState<Record<string, string>>({});
  const [overrideInput, setOverrideInput] = useState('');
  const overrideInputRef = useRef<HTMLInputElement>(null);
  // Focus the input whenever a new violation is shown.
  useEffect(() => {
    if (pendingPolicies.length > 0) {
      setTimeout(() => overrideInputRef.current?.focus(), 50);
    }
  }, [pendingPolicies.length]);
  const [draft, setDraft] = useState<Draft>(() => {
    const cohort = (fromCandidate?.suggestedCohort ?? 'C') as CohortLetter;
    return {
      firstName: fromCandidate?.firstName ?? '',
      lastName: fromCandidate?.lastName ?? '',
      cohort,
      telehealthAllowed: true,
      homeCommunityOk: true,
      preferredDays: [],
      // Default to "Auto-pick by capacity" so the engine balances the
      // nursing-group load by default; operator can still pick a specific
      // day from the dropdown.
      nursingDayAuto: true,
      requiredServices: CohortDefaultIndividualServices[cohort],
      intakeDate: todayLocalIso(),
      externalRef: fromCandidate?.externalRef,
      serviceConstraints: emptyServiceConstraints(),
    };
  });
  // Stale state guard: if the user navigates between candidates without
  // refreshing, sync draft when the candidate changes.
  useEffect(() => {
    if (!fromCandidate) return;
    const cohort = (fromCandidate.suggestedCohort ?? 'C') as CohortLetter;
    setDraft((d) => ({
      ...d,
      firstName: fromCandidate.firstName ?? d.firstName,
      lastName: fromCandidate.lastName ?? d.lastName,
      externalRef: fromCandidate.externalRef ?? d.externalRef,
      cohort,
      requiredServices: CohortDefaultIndividualServices[cohort],
    }));
  }, [fromCandidate?.candidateId]);

  // Fetch the full candidate when we arrived from the Pipeline. Gives us
  // sourceLabel + housingContext (when housing-sourced) so we can render
  // a context banner at the top of the wizard with location/room/phone/
  // move-in info pulled live from the bedboard.
  const { data: candidate } = useQuery({
    queryKey: ['intake-candidate', fromCandidate?.candidateId],
    queryFn: () =>
      api.get(`/intake-candidates/${fromCandidate!.candidateId}`).then((r) => r.data),
    enabled: !!fromCandidate?.candidateId,
  });

  // Seed the draft's clinic location from the housing pending-link's
  // pre-assigned clinicLocationId (set via the Housing Clients dropdown). This
  // is what lets the engine give a same-location provider priority during the
  // intake preview/commit for an In-Pipeline resident.
  const candidateClinicLocationId = candidate?.housingContext?.clinicLocationId ?? null;
  useEffect(() => {
    if (candidateClinicLocationId) {
      setDraft((d) => (d.locationId === candidateClinicLocationId ? d : { ...d, locationId: candidateClinicLocationId }));
    }
  }, [candidateClinicLocationId]);

  // Case manager dropdown options — COMMUNITY_CM and SUD_CM roles
  const { data: caseManagers } = useQuery({
    queryKey: ['case-manager-resources'],
    queryFn: () =>
      api
        .get('/resources', { params: { providerRoles: 'COMMUNITY_CM,SUD_CM', activeOnly: 'true' } })
        .then((r) => r.data),
  });

  // IOP track resources — active group resources of kind IOP.
  const { data: iopTracks } = useQuery({
    queryKey: ['iop-track-resources'],
    queryFn: () =>
      api
        .get('/resources', { params: { type: 'GROUP', activeOnly: 'true' } })
        .then((r) => (r.data as any[]).filter((g) => g.groupServiceType === 'IOP')),
  });

  const preview = useMutation({
    // Accepts an optional draft override so callers that just updated state
    // (e.g. applySuggestions) can re-preview with the new draft synchronously
    // instead of waiting for the React state batch to flush.
    mutationFn: (draftOverride?: Draft) =>
      api
        .post('/scheduling/preview', { draft: draftOverride ?? draft })
        .then((r) => r.data),
    // Fires at the START of every preview run (Preview button, candidate
    // auto-preview, applySuggestions). Resets the AI gate and bumps the epoch
    // so the AI checklist re-runs fresh and Commit re-disables until it's done.
    onMutate: () => {
      setAiComplete(false);
      setPreviewEpoch((e) => e + 1);
      qc.removeQueries({ queryKey: ['claude-review'] });
    },
    onSuccess: (data) => {
      if (data.locationMismatch && !draft.telehealthAllowed) {
        setShowTelehealthModal(true);
      }
    },
  });

  /**
   * Merge one or more AI-suggested actions into draft.serviceConstraints and
   * re-run the preview — but ONLY if the change does not create new hard
   * blockers or leave more services unscheduled than the current preview.
   *
   * Why dry-run first: an exclusion (e.g. "don't place NURSING_INDIVIDUAL on
   * FRI") is only useful if some OTHER day works. If the only viable slot was
   * the excluded one, the engine surfaces a HARD OUTSIDE_PROVIDER_AVAILABILITY
   * and the service goes unscheduled — i.e. Apply made the schedule strictly
   * worse, which is the opposite of what the operator clicked it for. So we
   * preview the candidate first and refuse to commit the constraint if any
   * metric regressed.
   *
   * Unresolvable EXCLUDE_RESOURCE actions (provider name not in current
   * preview audits) are skipped silently — the modal's Apply button is
   * already disabled for those.
   */
  async function applySuggestions(actions: AiSuggestionAction[]): Promise<boolean> {
    if (actions.length === 0) return false;
    const audits: any[] = preview.data?.resourceAudits ?? [];
    const findResourceId = (name: string): string | undefined => {
      const lower = name.trim().toLowerCase();
      return audits.find((a) => (a.resourceName ?? '').trim().toLowerCase() === lower)?.resourceId;
    };
    const cur = draft.serviceConstraints ?? emptyServiceConstraints();
    const next = cloneConstraints(cur);
    let appliedCount = 0;
    for (const a of actions) {
      if (a.kind === 'EXCLUDE_DAY') {
        const list = next.excludedDaysByService[a.serviceType] ?? [];
        if (!list.includes(a.day)) {
          next.excludedDaysByService[a.serviceType] = [...list, a.day];
          appliedCount++;
        }
      } else if (a.kind === 'EXCLUDE_RESOURCE') {
        const rid = findResourceId(a.resourceName);
        if (!rid) continue;
        const list = next.excludedResourcesByService[a.serviceType] ?? [];
        if (!list.includes(rid)) {
          next.excludedResourcesByService[a.serviceType] = [...list, rid];
          appliedCount++;
        }
      } else if (a.kind === 'AVOID_OVERLAP') {
        if (!next.avoidOverlapServices.includes(a.serviceType)) {
          next.avoidOverlapServices = [...next.avoidOverlapServices, a.serviceType];
          appliedCount++;
        }
      }
    }
    if (appliedCount === 0) return false;
    const candidateDraft: Draft = { ...draft, serviceConstraints: next };

    // Dry-run: preview the candidate without touching displayed state.
    let candidate: any;
    try {
      candidate = (await api.post('/scheduling/preview', { draft: candidateDraft })).data;
    } catch {
      toast.error('Could not preview the adjustment — please try again.');
      return false;
    }

    const baseHard = (preview.data?.violations ?? []).filter(
      (v: any) => v.severity === 'HARD',
    ).length;
    const baseUnsched: string[] = preview.data?.unscheduledServices ?? [];
    const newHard = (candidate.violations ?? []).filter(
      (v: any) => v.severity === 'HARD',
    ).length;
    const newUnsched: string[] = candidate.unscheduledServices ?? [];

    if (newHard > baseHard || newUnsched.length > baseUnsched.length) {
      const newlyUnscheduled = newUnsched.filter((s) => !baseUnsched.includes(s));
      const labels = newlyUnscheduled
        .map((s) => (ServiceLabels as Record<string, string>)[s] ?? s)
        .join(', ');
      const detail = labels
        ? `would leave ${labels} unschedulable`
        : `would create ${newHard - baseHard} new hard blocker(s)`;
      toast.error(
        `Can't apply — that adjustment ${detail}. Try a different suggestion, or remove an existing constraint first.`,
      );
      return false;
    }

    // Safe to commit. Re-mutate so preview.data reflects the new schedule.
    setDraft(candidateDraft);
    preview.mutate(candidateDraft);
    toast.success(`Applied ${appliedCount} suggestion${appliedCount > 1 ? 's' : ''}.`);
    return true;
  }

  /**
   * Dry-run a single AI-suggested action without mutating state — returns true
   * if applying it would not regress the schedule (no new HARD blockers, no
   * extra unscheduled services). Used by the checklist UI to HIDE suggestions
   * that the engine can't actually act on, so the operator never sees an
   * "Apply" button that would refuse.
   */
  async function canApplyAction(action: AiSuggestionAction): Promise<boolean> {
    const audits: any[] = preview.data?.resourceAudits ?? [];
    const findResourceId = (name: string): string | undefined => {
      const lower = name.trim().toLowerCase();
      return audits.find((a) => (a.resourceName ?? '').trim().toLowerCase() === lower)?.resourceId;
    };
    const cur = draft.serviceConstraints ?? emptyServiceConstraints();
    const next = cloneConstraints(cur);
    if (action.kind === 'EXCLUDE_DAY') {
      const list = next.excludedDaysByService[action.serviceType] ?? [];
      if (list.includes(action.day)) return true; // already applied → safe
      next.excludedDaysByService[action.serviceType] = [...list, action.day];
    } else if (action.kind === 'EXCLUDE_RESOURCE') {
      const rid = findResourceId(action.resourceName);
      if (!rid) return false; // can't resolve provider in this preview
      const list = next.excludedResourcesByService[action.serviceType] ?? [];
      if (list.includes(rid)) return true;
      next.excludedResourcesByService[action.serviceType] = [...list, rid];
    } else if (action.kind === 'AVOID_OVERLAP') {
      if (next.avoidOverlapServices.includes(action.serviceType)) return true;
      next.avoidOverlapServices = [...next.avoidOverlapServices, action.serviceType];
    }
    const candidateDraft: Draft = { ...draft, serviceConstraints: next };
    try {
      const candidate = (
        await api.post('/scheduling/preview', { draft: candidateDraft })
      ).data;
      const baseHard = (preview.data?.violations ?? []).filter(
        (v: any) => v.severity === 'HARD',
      ).length;
      const baseUnsched: string[] = preview.data?.unscheduledServices ?? [];
      const newHard = (candidate.violations ?? []).filter(
        (v: any) => v.severity === 'HARD',
      ).length;
      const newUnsched: string[] = candidate.unscheduledServices ?? [];
      return !(newHard > baseHard || newUnsched.length > baseUnsched.length);
    } catch {
      return false;
    }
  }

  /** Remove a single applied constraint (chip × button) and re-preview. */
  function clearConstraint(
    kind: 'excludedDay' | 'excludedResource' | 'avoidOverlap',
    serviceType: string,
    value: string,
  ) {
    const cur = draft.serviceConstraints ?? emptyServiceConstraints();
    const next = cloneConstraints(cur);
    if (kind === 'excludedDay') {
      const list = (cur.excludedDaysByService[serviceType] ?? []).filter((d) => d !== value);
      if (list.length === 0) delete next.excludedDaysByService[serviceType];
      else next.excludedDaysByService[serviceType] = list;
    } else if (kind === 'excludedResource') {
      const list = (cur.excludedResourcesByService[serviceType] ?? []).filter((id) => id !== value);
      if (list.length === 0) delete next.excludedResourcesByService[serviceType];
      else next.excludedResourcesByService[serviceType] = list;
    } else {
      // avoidOverlap — value is the serviceType itself
      next.avoidOverlapServices = next.avoidOverlapServices.filter((s) => s !== serviceType);
    }
    const newDraft: Draft = { ...draft, serviceConstraints: next };
    setDraft(newDraft);
    preview.mutate(newDraft);
  }

  const commit = useMutation({
    mutationFn: (overrides?: Record<string, string>) =>
      api
        .post(
          '/scheduling/commit',
          {
            // Pass `candidateId` through so the server can mark the
            // originating IntakeCandidate as CONVERTED even when it has no
            // EMR externalRef (housing-sourced candidates).
            draft: { ...draft, candidateId: fromCandidate?.candidateId },
            overrideReasons: overrides,
          },
          { _suppressGlobalError: true } as any,
        )
        .then((r) => r.data),
    onSuccess: (data) => {
      setCommitErrorMsg(null);
      toast.success(`Client created: ${data.cohortId}`);
      navigate(`/clients/${data.clientId}`);
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      const status = err?.response?.status ?? 0;
      let msg: string;

      if (status >= 500 || !data) {
        msg = 'Something went wrong on our end. Please try again. If the problem continues, contact support.';
      } else if (data?.violations?.length > 0) {
        // Structured violation errors from the scheduling engine
        const violations: Array<{ code: string; message: string; serviceType?: string }> =
          data.violations;
        const lines = violations.map((v) => {
          // Translate known engine codes into plain English
          if (v.code === 'OUTSIDE_PROVIDER_AVAILABILITY') {
            const svc = v.serviceType ? ` for ${v.serviceType.replace(/_/g, ' ').toLowerCase()}` : '';
            return `No available provider slot was found${svc}. Try adjusting the service dates or enabling Telehealth.`;
          }
          if (v.code === 'GROUP_OVER_CAPACITY') {
            return `A group session is full. An admin can override this with a reason.`;
          }
          if (v.code === 'MISSING_REQUIRED_GROUP_RESOURCE') {
            return `A required group (${v.serviceType ?? ''}) has no active resource set up. Please contact your administrator.`;
          }
          if (v.code === 'GROUP_UNDERSTAFFED') {
            return `A group session has no staff assigned. Please contact your administrator.`;
          }
          // Fall back to the raw message but strip technical jargon
          return v.message.replace(/^HARD: /i, '').replace(/^POLICY: /i, '');
        });
        msg = lines.join('\n');
      } else if (typeof data?.message === 'string') {
        // Map known top-level messages
        if (data.message.includes('capacity')) {
          msg = data.message; // already human-readable (set explicitly in the service)
        } else if (data.message.includes('Client not found')) {
          msg = 'The client record could not be found. Please go back and try again.';
        } else {
          msg = 'The intake could not be committed. Please go back, check the details, and try again.';
        }
      } else {
        msg = 'The intake could not be saved. Please try again.';
      }

      setCommitErrorMsg(msg);
    },
  });

  function updateDraft<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => {
      const next = { ...d, [key]: value };
      if (key === 'cohort') {
        // Prefer the master library row's defaults; fall back to the enum so
        // a stale or empty cohort list never blanks out required services.
        const fromLib = cohortByCode.get(value as string)?.defaultIndividualServices;
        next.requiredServices =
          fromLib && fromLib.length > 0
            ? (fromLib as IndividualServiceType[])
            : CohortDefaultIndividualServices[value as CohortLetter] ?? [];
        // Changing cohort resets the "Not Needed" override — each cohort has
        // its own default service set and the operator should opt out explicitly.
        next.servicesNotNeeded = false;
        // Clear IOP track pin when the new cohort doesn't need one — otherwise
        // a stale UUID from a prior cohort selection leaks into the payload.
        // We derive "needs IOP track" from the master library row.
        const needsIop =
          (cohortByCode.get(value as string)?.requiredGroups ?? []).includes('IOP');
        if (!needsIop) next.iopTrackResourceId = undefined;
      }
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">New Intake</h1>
          <p className="text-sm text-slate-500">Step {step} of 2 · Cohort assignment is automatic.</p>
        </div>
        <ol className="flex gap-4 text-sm text-slate-500">
          <li className={step >= 1 ? 'font-semibold text-brand-700' : ''}>1 · Profile</li>
          <li className={step >= 2 ? 'font-semibold text-brand-700' : ''}>2 · Preview & Commit</li>
        </ol>
      </header>

      {/* Housing context banner — shown when this intake was promoted from
          a housing bedboard row. Surfaces the resident's location, room,
          phone, and program text so the operator can complete intake
          without bouncing to /housing. */}
      {candidate?.housingContext && (
        <section className="card border-l-4 border-l-emerald-500 p-4">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
            🏠 Housing context
            <span className="pill-info">
              {candidate.housingContext.locationDisplayName}
            </span>
            <span className="text-2xs text-ink-subtle">
              from bedboard sync · {candidate.sourceLabel ?? 'Housing'}
            </span>
          </h2>
          <div className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
            <div>
              <span className="text-ink-subtle">Address:</span>{' '}
              {candidate.housingContext.address || <em className="text-ink-subtle">—</em>}
            </div>
            <div>
              <span className="text-ink-subtle">Room:</span>{' '}
              <strong>{candidate.housingContext.rawRoom || '—'}</strong>
            </div>
            <div>
              <span className="text-ink-subtle">Phone (bedboard):</span>{' '}
              {candidate.housingContext.rawPhone ? (
                <a className="font-mono hover:underline" href={`tel:${candidate.housingContext.rawPhone}`}>
                  {candidate.housingContext.rawPhone}
                </a>
              ) : (
                <em className="text-ink-subtle">—</em>
              )}
            </div>
            <div>
              <span className="text-ink-subtle">Move-in date:</span>{' '}
              <strong>{candidate.housingContext.rawMoveInDate || '—'}</strong>
            </div>
            <div>
              <span className="text-ink-subtle">Program label (bedboard):</span>{' '}
              <em>{candidate.housingContext.rawProgram || '—'}</em>
            </div>
            {candidate.housingContext.houseManagerName && (
              <div>
                <span className="text-ink-subtle">House manager:</span>{' '}
                {candidate.housingContext.houseManagerName}
                {candidate.housingContext.houseManagerPhone && (
                  <>
                    {' · '}
                    <a className="font-mono hover:underline" href={`tel:${candidate.housingContext.houseManagerPhone}`}>
                      {candidate.housingContext.houseManagerPhone}
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
          <p className="mt-2 text-2xs text-ink-subtle">
            After you commit this intake, the next housing sync will auto-link the bedboard row
            to the new Client, and a permanent <strong>Housing card</strong> will appear on the
            client's profile with these fields (plus any updates from the bedboard).
          </p>
        </section>
      )}

      {step === 1 && (
        <div className="card space-y-4 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" required>
              <input
                className="input"
                value={draft.firstName}
                onChange={(e) => updateDraft('firstName', e.target.value)}
              />
            </Field>
            <Field label="Last name" required>
              <input
                className="input"
                value={draft.lastName}
                onChange={(e) => updateDraft('lastName', e.target.value)}
              />
            </Field>
            <Field label="Cohort" required>
              <select
                className="input"
                value={draft.cohort}
                onChange={(e) => updateDraft('cohort', e.target.value as CohortLetter)}
              >
                {cohortList.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Intake date">
              <input
                type="date"
                className="input"
                value={draft.intakeDate}
                onChange={(e) => updateDraft('intakeDate', e.target.value)}
              />
            </Field>
            {(cohortByCode.get(draft.cohort)?.requiredGroups ?? []).includes('IOP') && (
              <Field label="IOP track">
                <select
                  className="input"
                  value={draft.iopTrackResourceId ?? ''}
                  onChange={(e) =>
                    updateDraft('iopTrackResourceId', e.target.value || undefined)
                  }
                >
                  <option value="">— Auto-pick by capacity —</option>
                  {(iopTracks ?? []).map((g: any) => (
                    <option key={g.id} value={g.id}>
                      {g.displayName} ({formatRange(g.defaultStartMin, g.defaultEndMin)})
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Nursing group day">
              <select
                className="input"
                value={draft.nursingSkip ? '__SKIP__' : draft.nursingDayAuto ? '__AUTO__' : (draft.nursingGroupDay ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setDraft((d) =>
                    v === '__SKIP__'
                      ? { ...d, nursingSkip: true, nursingDayAuto: false, nursingGroupDay: undefined }
                      : v === '__AUTO__'
                        ? { ...d, nursingSkip: false, nursingDayAuto: true, nursingGroupDay: undefined }
                        : {
                            ...d,
                            nursingSkip: false,
                            nursingDayAuto: false,
                            nursingGroupDay: (v || undefined) as Draft['nursingGroupDay'],
                          },
                  );
                }}
              >
                <option value="">Unassigned</option>
                <option value="__AUTO__">Auto-pick by capacity</option>
                <option value="__SKIP__">Not Needed</option>
                {['MON', 'TUE', 'WED', 'THU', 'FRI'].map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </Field>
            <Field label="EMR client ID">
              <input
                className="input"
                placeholder="Paste from EMR (e.g., 18A7C3-9F2)"
                value={draft.externalRef ?? ''}
                onChange={(e) => updateDraft('externalRef', e.target.value || undefined)}
              />
            </Field>
            <Field label="Assigned case manager">
              <select
                className="input"
                value={draft.caseManagerResourceId ?? ''}
                onChange={(e) =>
                  updateDraft('caseManagerResourceId', e.target.value || undefined)
                }
              >
                <option value="">— Unassigned —</option>
                {(caseManagers ?? []).map((cm: any) => (
                  <option key={cm.id} value={cm.id}>
                    {cm.displayName}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <ToggleField label="Telehealth allowed" value={draft.telehealthAllowed} onChange={(v) => updateDraft('telehealthAllowed', v)} />
            <ToggleField label="Home / community OK" value={draft.homeCommunityOk} onChange={(v) => updateDraft('homeCommunityOk', v)} />
          </div>

          <Field label="Required individual services">
            <div className="mb-2">
              <label className="flex items-center gap-2 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={draft.servicesNotNeeded ?? false}
                  onChange={(e) => {
                    const notNeeded = e.target.checked;
                    setDraft((d) => ({
                      ...d,
                      servicesNotNeeded: notNeeded,
                      requiredServices: notNeeded ? [] : CohortDefaultIndividualServices[d.cohort] ?? [],
                    }));
                  }}
                />
                <span>Not Needed</span>
              </label>
            </div>
            <div className={`grid grid-cols-2 gap-2 sm:grid-cols-3 ${draft.servicesNotNeeded ? 'pointer-events-none opacity-40' : ''}`}>
              {IndividualServiceTypes.map((s) => (
                <label key={s} className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.requiredServices.includes(s)}
                    disabled={draft.servicesNotNeeded}
                    onChange={(e) =>
                      updateDraft(
                        'requiredServices',
                        e.target.checked
                          ? [...draft.requiredServices, s]
                          : draft.requiredServices.filter((r) => r !== s),
                      )
                    }
                  />
                  <span>{ServiceLabels[s]}</span>
                </label>
              ))}
            </div>
          </Field>

          <div className="flex justify-end">
            <button
              className="btn-primary"
              disabled={!draft.firstName || !draft.lastName}
              onClick={() => {
                preview.mutate(undefined);
                setStep(2);
              }}
            >
              Preview schedule →
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <button className="btn-secondary" onClick={() => setStep(1)}>
              ← Back to edit
            </button>
            {preview.data && (
              <div className="flex items-center gap-2">
                <button
                  className="btn-secondary"
                  onClick={() => aiSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  View AI Analysis
                </button>
                <button
                  className="btn-primary"
                  disabled={
                    commit.isPending ||
                    preview.isPending ||
                    !aiComplete ||
                    (preview.data.violations?.some((v: any) => v.severity === 'HARD') ?? false)
                  }
                  title={
                    !aiComplete
                      ? 'Waiting for the AI analysis (GPT-4o + Claude) to complete…'
                      : (preview.data.violations?.some((v: any) => v.severity === 'HARD') ?? false)
                      ? 'Resolve all hard blockers before committing'
                      : undefined
                  }
                  onClick={() => {
                    setCommitErrorMsg(null);
                    const policy: Array<{ code: string; message: string }> =
                      preview.data.violations?.filter((v: any) => v.severity === 'POLICY') ?? [];
                    if (policy.length === 0) {
                      commit.mutate({});
                    } else {
                      setCollectedReasons({});
                      setOverrideInput('');
                      setPendingPolicies(policy);
                    }
                  }}
                >
                  {commit.isPending
                    ? 'Committing…'
                    : !aiComplete
                    ? 'Awaiting AI analysis…'
                    : 'Commit intake'}
                </button>
              </div>
            )}
          </div>

          {preview.isPending && <div className="card p-6 text-center text-sm text-slate-500">Running scheduler…</div>}
          {preview.data && (
            <IntakePreview
              key={previewEpoch}
              data={preview.data}
              serviceConstraints={draft.serviceConstraints}
              onApplySuggestions={applySuggestions}
              onClearConstraint={clearConstraint}
              validateApplicability={canApplyAction}
              previewEpoch={previewEpoch}
              onAiStatusChange={setAiComplete}
              aiSectionRef={aiSectionRef}
            />
          )}
          {preview.error && (
            <div className="card border-rose-300 bg-rose-50 p-4 text-sm text-rose-700">
              Preview failed: {(preview.error as any).message}
            </div>
          )}
          {commitErrorMsg && (
            <div className="flex items-start gap-3 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3">
              <span className="mt-0.5 text-base">❌</span>
              <div className="flex-1">
                <p className="font-semibold text-rose-800">Intake could not be saved</p>
                {commitErrorMsg.includes('\n') ? (
                  <ul className="mt-1 list-disc pl-4 text-sm text-rose-700">
                    {commitErrorMsg.split('\n').map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-0.5 text-sm text-rose-700">{commitErrorMsg}</p>
                )}
              </div>
              <button
                className="shrink-0 text-rose-400 hover:text-rose-600"
                onClick={() => setCommitErrorMsg(null)}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      {/* Telehealth suggestion modal — shown when preview finds OUTSIDE_PROVIDER_AVAILABILITY
          hard violations and telehealth is currently disabled. Gives the operator a one-click
          path to enable it and re-run the preview. */}
      {showTelehealthModal && (
        <Modal
          open
          onClose={() => setShowTelehealthModal(false)}
          title="No suitable provider found"
          size="sm"
          footer={
            <>
              <button
                className="btn-secondary"
                onClick={() => setShowTelehealthModal(false)}
              >
                Dismiss
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowTelehealthModal(false);
                  const newDraft = { ...draft, telehealthAllowed: true };
                  setDraft(newDraft);
                  preview.mutate(newDraft);
                }}
              >
                Enable Telehealth &amp; Re-preview
              </button>
            </>
          }
        >
          <p className="text-sm text-slate-700">
            No providers are available at this client's clinic location. All services were
            placed with providers at a different location.
          </p>
          <p className="mt-2 text-sm text-slate-700">
            Consider enabling <strong>Telehealth</strong> so the client can be seen remotely
            by their assigned providers. Would you like to enable it and re-run the preview?
          </p>
        </Modal>
      )}

      {/* Policy override modal — shown when committing a schedule that has
          POLICY violations. Presents each violation in turn; the operator
          must enter a reason before the next one is shown. On the last one,
          the commit fires with all collected reasons. Cancelling at any point
          aborts the commit entirely. */}
      {pendingPolicies.length > 0 && (() => {
        const current = pendingPolicies[0]!;
        const total = Object.keys(collectedReasons).length + pendingPolicies.length;
        const done = Object.keys(collectedReasons).length;
        const handleConfirm = () => {
          const reason = overrideInput.trim();
          if (!reason) return;
          const next = { ...collectedReasons, [current.code]: reason };
          const remaining = pendingPolicies.slice(1);
          if (remaining.length === 0) {
            setPendingPolicies([]);
            setCollectedReasons({});
            setOverrideInput('');
            commit.mutate(next);
          } else {
            setCollectedReasons(next);
            setOverrideInput('');
            setPendingPolicies(remaining);
          }
        };
        return (
          <Modal
            open
            onClose={() => { setPendingPolicies([]); setCollectedReasons({}); setOverrideInput(''); }}
            title="Policy override required"
            subtitle={total > 1 ? `${done + 1} of ${total}` : undefined}
            size="sm"
            footer={
              <>
                <button
                  className="btn-secondary"
                  onClick={() => { setPendingPolicies([]); setCollectedReasons({}); setOverrideInput(''); }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  disabled={!overrideInput.trim()}
                  onClick={handleConfirm}
                >
                  {pendingPolicies.length === 1 ? 'Confirm & commit' : 'Next →'}
                </button>
              </>
            }
          >
            <div className="space-y-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
                <p className="font-medium text-amber-800">{current.code.replace(/_/g, ' ')}</p>
                <p className="mt-0.5 text-amber-700">{current.message}</p>
              </div>
              <div>
                <label className="label">Override reason <span className="text-rose-500">*</span></label>
                <input
                  ref={overrideInputRef}
                  className="input"
                  placeholder="Enter reason for overriding this policy…"
                  value={overrideInput}
                  onChange={(e) => setOverrideInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && overrideInput.trim()) handleConfirm(); }}
                />
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

function formatRange(startMin: number | null, endMin: number | null): string {
  if (startMin == null || endMin == null) return '';
  const fmt = (m: number) => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    const ampm = h < 12 ? 'a' : 'p';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return mm === 0 ? `${h12}${ampm}` : `${h12}:${String(mm).padStart(2, '0')}${ampm}`;
  };
  return `${fmt(startMin)}–${fmt(endMin)}`;
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="label">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function ToggleField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded border border-slate-200 px-3 py-2">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm">{label}</span>
    </label>
  );
}
